import { useState, useRef, useEffect, lazy, Suspense, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { detectAndConvert } from "@/lib/keyboardMapper";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, ShoppingCart, Heart, User, Menu, X, ChevronDown,
  Store, LogOut, ShieldCheck,
  Sun, Moon, Camera, Package, Ticket, Coins, Star, Headphones,
  Settings, ChevronLeft, ArrowLeftRight, Sparkles, Download, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useDynamicCategories } from "@/hooks/useDynamicCategories";
import { useSubcategoryImages } from "@/hooks/useSubcategoryImages";
import { useAuth } from "@/hooks/useAuth";
import { useCart } from "@/hooks/useCart";
import { useTheme } from "@/hooks/useTheme";
import { usePoints } from "@/hooks/usePoints";
import { useWishlist } from "@/hooks/useWishlist";
import PointsBadge from "@/components/PointsBadge";
const DailyReward = lazy(() => import("@/components/DailyReward"));
import SubcategoryCircle from "@/components/SubcategoryCircle";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import nicegiftLogo from "@/assets/nicegift-logo.png";
import nicegiftLogoDark from "@/assets/logo-dark.png";
const VisualSearchModal = lazy(() => import("@/components/VisualSearchModal"));
const NotificationsDropdown = lazy(() => import("@/components/NotificationsDropdown"));
const UserNotificationsDropdown = lazy(() => import("@/components/UserNotificationsDropdown"));
const LanguageSwitcher = lazy(() => import("@/components/LanguageSwitcher"));
import { supabase } from "@/integrations/supabase/client";
import { productUrl } from "@/lib/productUrl";

// Categories loaded dynamically from useDynamicCategories hook

const NavIconButton = ({ children, className = "", title, ...props }: any) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    className={`p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-xl hover:bg-primary/10 transition-all duration-200 relative group hover:shadow-[0_0_12px_hsl(var(--primary)/0.2)] ${className}`}
    title={title}
    aria-label={props['aria-label'] || title}
    {...props}
  >
    {children}
  </motion.button>
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [visualSearchOpen, setVisualSearchOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [hoveredCat, setHoveredCat] = useState<number | null>(null);
  const [mobileExpandedCats, setMobileExpandedCats] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchPreview, setShowSearchPreview] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const mobileSearchRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const { user, isVendor, isAdmin: isAdminRole, isProductEditor, signOut } = useAuth();
  // Product editors (scoped vendor managers) also get the admin panel entry point
  const isAdmin = isAdminRole || isProductEditor;
  const { itemCount, setIsOpen: openCart } = useCart();
  const { theme, toggleTheme, isDark } = useTheme();
  const { balance: pointsBalance } = usePoints();
  const { count: wishlistCount } = useWishlist();
  const { data: dynamicCategories } = useDynamicCategories();
  const navigate = useNavigate();
  const location = useLocation();

  // Live search preview
  const { data: searchPreviewResults } = useQuery({
    queryKey: ["search-preview", searchQuery],
    queryFn: async () => {
      if (!searchQuery.trim() || searchQuery.trim().length < 2) return [];
      const raw = searchQuery.trim();
      const converted = detectAndConvert(raw);
      const q = converted || raw;
      const { data } = await supabase
        .from("products")
        .select("id, name, price, original_price, image_url, category")
        .eq("is_published", true).gt("stock", 0)
        .gt("price", 0)
        .or(`name.ilike.%${q}%,short_description.ilike.%${q}%,category.ilike.%${q}%`)
        .order("sold_count", { ascending: false })
        .limit(6);
      return data || [];
    },
    enabled: searchQuery.trim().length >= 2,
    staleTime: 30000,
  });

  const [navigatingProductId, setNavigatingProductId] = useState<string | null>(null);

  const handleSearchSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/חיפוש?q=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchPreview(false);
      setSearchFocused(false);
      setMobileSearchOpen(false);
      setSearchQuery("");
    }
  }, [searchQuery, navigate]);

  const handleMobileProductSelect = useCallback((productId: string, productName?: string) => {
    if (navigatingProductId) return;
    setNavigatingProductId(productId);
    toast.loading("פותח את המוצר...", { id: "search-nav", description: productName });
    const target = `/product/${encodeURIComponent(productId)}`;
    setShowSearchPreview(false);
    requestAnimationFrame(() => {
      navigate(target);
      setMobileSearchOpen(false);
      setSearchQuery("");
      setNavigatingProductId(null);
      toast.dismiss("search-nav");
    });
  }, [navigate, navigatingProductId]);


  // Close preview on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchPreview(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Collect all subcategory names for image fetching
  const allSubNames = useMemo(() => {
    if (!dynamicCategories) return [];
    return dynamicCategories.flatMap(g => g.subcategories.map(s => s.name));
  }, [dynamicCategories]);

  const { data: subImages } = useSubcategoryImages(allSubNames);

  // Build categories array from dynamic data with subcategory image mapping
  const categories = useMemo(() => {
    if (!dynamicCategories) return [];
    return dynamicCategories.map(g => {
      // Use first subcategory image as parent circle image fallback
      let parentCircle = g.circleImage;
      if ((!parentCircle || parentCircle.includes("placeholder")) && subImages) {
        for (const s of g.subcategories) {
          const img = subImages.get(s.name);
          if (img) { parentCircle = img; break; }
        }
      }
      return {
        name: g.name,
        slug: g.slug,
        icon: g.icon,
        color: g.color,
        circleImage: parentCircle,
        subcategories: g.subcategories.map(s => ({
          name: s.name,
          emoji: s.emoji,
          staticImage: s.image,
          dbImage: subImages?.get(s.name),
        })),
      };
    });
  }, [dynamicCategories, subImages]);

  // Detect active category from /קטגוריה/:slug URL
  const categorySlugMatch = decodeURIComponent(location.pathname).match(/^\/קטגוריה\/([^/]+)/);
  const activeCatSlug = categorySlugMatch ? categorySlugMatch[1] : null;
  const activeCatIndex = activeCatSlug ? categories.findIndex(c => c.slug === activeCatSlug) : -1;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (profileMenuRef.current && !profileMenuRef.current.contains(target)) {
        // Also check if click was on a trigger button
        const triggers = document.querySelectorAll('.profile-menu-trigger');
        for (const trigger of triggers) {
          if (trigger.contains(target)) return;
        }
        setProfileMenuOpen(false);
      }
    };
    if (profileMenuOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [profileMenuOpen]);

  return (
    <header className="sticky top-0 z-50">
      {/* ── Top promo bar ── */}
      <div className="relative overflow-hidden bg-[hsl(220,45%,12%)] dark:bg-[hsl(220,50%,8%)] shadow-[0_2px_12px_hsl(200,90%,48%/0.15)] dark:shadow-[0_2px_16px_hsl(200,90%,48%/0.25)]">
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(220,60%,15%/0.5)] via-[hsl(200,80%,30%/0.15)] to-[hsl(220,60%,15%/0.5)] dark:from-[hsl(220,60%,6%/0.5)] dark:via-[hsl(200,80%,25%/0.2)] dark:to-[hsl(220,60%,6%/0.5)]" />
        <div className="container flex items-center justify-between py-1.5 md:py-2 relative">
          <div className="hidden md:flex items-center gap-4 text-[11px] text-[hsl(210,20%,70%)] dark:text-[hsl(210,20%,55%)]">
            <Link to="/אודות" className="hover:text-white transition-colors">אודות</Link>
            <Link to="/צור-קשר" className="hover:text-white transition-colors">צור קשר</Link>
            <Link to="/מרכז-עזרה" className="hover:text-white transition-colors">עזרה</Link>
          </div>
          <p className="text-[12px] sm:text-xs font-medium text-[hsl(0,0%,92%)] dark:text-[hsl(0,0%,88%)] tracking-wide mx-auto md:mx-0">
            <span className="inline-flex items-center gap-2">
              <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 2, repeat: Infinity }}>🔥</motion.span>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="hover:text-white transition-colors underline decoration-dotted underline-offset-2 cursor-pointer">משלוח חינם מעל ₪500</button>
                </PopoverTrigger>
                <PopoverContent className="w-72 text-sm p-4" side="bottom">
                  <p className="font-bold text-foreground mb-1">🚚 משלוח חינם מעל ₪500</p>
                  <p className="text-muted-foreground text-xs leading-relaxed">קנית מוצרים מעל 500 ₪ מזכה אותך במשלוח חינם למוצרים מקבוצה אחת (אותו ספק/מחסן). ההטבה מחושבת אוטומטית בקופה.</p>
                </PopoverContent>
              </Popover>
              <span className="text-[hsl(210,20%,40%)] mx-1">•</span>
              <span>🎁 <span className="font-black text-primary">50 נקודות מתנה</span> לכל משתמש חדש!</span>
            </span>
          </p>
          <div className="hidden md:flex items-center gap-3">
          </div>
        </div>
      </div>

      {/* ── Main nav ── */}
      <nav className="border-b border-border/20 bg-card shadow-[0_1px_6px_rgba(0,0,0,0.04)]">
        <div className="container flex items-center gap-2 md:gap-6 py-2 md:py-3">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <motion.img
              src={isDark ? nicegiftLogoDark : nicegiftLogo}
              alt="NiceGift"
              width={120}
              height={56}
              className="h-9 md:h-14 w-auto object-contain"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            />
          </Link>

          {/* Search bar — desktop only */}
          <div ref={searchRef} className="hidden md:flex flex-1 max-w-xl mx-auto relative">
            <motion.div
              className="w-full"
              animate={{ scale: searchFocused ? 1.005 : 1 }}
              transition={{ duration: 0.2 }}
            >
              <form
                className="relative w-full group"
                onSubmit={handleSearchSubmit}
              >
                <input
                  type="text"
                  name="query"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setShowSearchPreview(true); }}
                  placeholder="חפש מוצרים, קטגוריות, מותגים..."
                  className={`w-full rounded-xl border bg-muted/30 py-2.5 pr-5 pl-20 text-sm outline-none transition-all duration-300 placeholder:text-muted-foreground/40 ${
                    searchFocused
                      ? "border-primary/50 bg-card shadow-[0_0_0_3px_hsl(var(--primary)/0.06),0_4px_16px_hsl(var(--primary)/0.04)]"
                      : "border-border/30 hover:border-border/50 hover:bg-muted/50"
                  }`}
                  onFocus={() => { setSearchFocused(true); if (searchQuery.trim().length >= 2) setShowSearchPreview(true); }}
                  onBlur={() => setSearchFocused(false)}
                />
                <div className="absolute left-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setVisualSearchOpen(true)}
                    className="rounded-lg p-2 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                    title="חיפוש לפי תמונה"
                  >
                    <Camera size={15} />
                  </motion.button>
                  <motion.button
                    type="submit"
                    aria-label="חיפוש"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="gradient-primary rounded-lg p-2 text-primary-foreground shadow-sm shadow-primary/20 hover:shadow-md hover:shadow-primary/30 transition-shadow"
                  >
                    <Search size={15} />
                  </motion.button>
                </div>
              </form>
            </motion.div>

            {/* Live preview dropdown */}
            <AnimatePresence>
              {showSearchPreview && searchPreviewResults && searchPreviewResults.length > 0 && searchQuery.trim().length >= 2 && (
                <motion.div
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-full mt-1 left-0 right-0 bg-card border border-border/40 rounded-xl shadow-xl z-[100] overflow-hidden"
                >
                  {searchPreviewResults.map((product) => {
                    const discount = product.original_price > product.price
                      ? Math.round((1 - product.price / product.original_price) * 100)
                      : 0;
                    return (
                      <Link
                        key={product.id}
                        to={productUrl(product.name)}
                        onClick={() => { setShowSearchPreview(false); setSearchQuery(""); }}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 transition-colors border-b border-border/10 last:border-0"
                      >
                        <img
                          src={product.image_url || "/placeholder.svg"}
                          alt=""
                          className="w-10 h-10 rounded-lg object-contain bg-muted/10 shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium line-clamp-1">{product.name}</p>
                          <p className="text-[10px] text-muted-foreground">{product.category}</p>
                        </div>
                        <div className="text-left shrink-0">
                          <span className="text-xs font-bold text-[hsl(var(--price))]">₪{product.price}</span>
                          {discount > 0 && (
                            <span className="text-[9px] text-muted-foreground line-through mr-1">₪{product.original_price}</span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                  <button
                    onClick={() => handleSearchSubmit()}
                    className="w-full px-3 py-2.5 text-xs font-medium text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-1"
                  >
                    <Search size={12} />
                    הצג את כל התוצאות עבור "{searchQuery}"
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Actions — mobile top bar */}
          <div className="flex md:hidden items-center gap-0.5">
            <NavIconButton onClick={() => setMobileSearchOpen(true)} title="חיפוש">
              <Search size={18} />
            </NavIconButton>

            <NavIconButton onClick={() => setVisualSearchOpen(true)} title="חיפוש לפי תמונה">
              <Camera size={18} />
            </NavIconButton>

            <NavIconButton onClick={toggleTheme} title={isDark ? "מצב יום" : "מצב לילה"}>
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={theme}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.25 }}
                >
                  {isDark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} />}
                </motion.div>
              </AnimatePresence>
            </NavIconButton>

            {/* Language switcher moved to side tab */}

            {isAdmin && location.pathname.startsWith("/ניהול") ? (
              <NotificationsDropdown variant="navbar" />
            ) : user ? (
              <UserNotificationsDropdown />
            ) : null}

            <NavIconButton className="relative" onClick={() => navigate("/מועדפים")} aria-label="מועדפים">
              <Heart size={18} />
              {wishlistCount > 0 && (
                <motion.span
                  key={wishlistCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                  className="absolute -top-0.5 -left-0.5 gradient-hot text-primary-foreground text-[9px] font-bold rounded-full flex items-center justify-center min-w-[16px] h-[16px] shadow-sm"
                >
                  {wishlistCount}
                </motion.span>
              )}
            </NavIconButton>

            {user ? (
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setProfileMenuOpen((v) => !v)}
                  className="flex items-center gap-1.5 py-1 px-2 rounded-xl hover:bg-muted/60 transition-all duration-200 profile-menu-trigger"
                >
                  <div className="w-7 h-7 rounded-full gradient-primary text-primary-foreground flex items-center justify-center text-[10px] font-black">
                    {(user.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] text-muted-foreground leading-none">שלום 👋</p>
                    <p className="text-[10px] font-bold text-foreground leading-tight mt-0.5 max-w-[60px] truncate">
                      {user.email?.split("@")[0] || "משתמש"}
                    </p>
                  </div>
                </motion.button>
              </div>
            ) : (
              <Link to="/כניסה" aria-label="כניסה לחשבון">
                <NavIconButton aria-label="כניסה לחשבון"><User size={18} /></NavIconButton>
              </Link>
            )}
          </div>

          {/* Actions — desktop */}
          <div className="hidden md:flex items-center gap-1">
            {/* Notifications */}
            {isAdmin && location.pathname.startsWith("/ניהול") ? (
              <NotificationsDropdown variant="navbar" />
            ) : user ? (
              <UserNotificationsDropdown />
            ) : null}

            {/* Wishlist */}
            <NavIconButton className="relative" onClick={() => navigate("/מועדפים")} aria-label="מועדפים">
              <Heart size={19} />
              {wishlistCount > 0 && (
                <motion.span
                  key={wishlistCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                  className="absolute -top-0.5 -left-0.5 gradient-hot text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center min-w-[17px] h-[17px] shadow-sm"
                >
                  {wishlistCount}
                </motion.span>
              )}
            </NavIconButton>

            {/* Cart */}
            <NavIconButton className="relative" onClick={() => openCart(true)} aria-label="עגלת קניות">
              <ShoppingCart size={19} />
              {itemCount > 0 && (
                <motion.span
                  key={itemCount}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 500 }}
                  className="absolute -top-0.5 -left-0.5 gradient-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center min-w-[17px] h-[17px] shadow-sm"
                >
                  {itemCount}
                </motion.span>
              )}
            </NavIconButton>

            {/* Separator */}
            <div className="w-px h-6 bg-border/40 mx-1" />

            {user ? (
              <div className="flex items-center gap-1">
                <PointsBadge />
                <Suspense fallback={null}><DailyReward /></Suspense>
                {/* ═══ Profile trigger (desktop) ═══ */}
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setProfileMenuOpen((v) => !v)}
                    className="flex items-center gap-2.5 py-1.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 profile-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full gradient-primary text-primary-foreground flex items-center justify-center text-xs font-black ring-2 ring-primary/15 ring-offset-1 ring-offset-card shadow-sm">
                      {(user.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="hidden lg:block text-right">
                      <p className="text-[11px] text-muted-foreground leading-none">שלום 👋</p>
                      <p className="text-[12px] font-bold text-foreground leading-tight mt-0.5 max-w-[100px] truncate">
                        {user.email?.split("@")[0] || "משתמש"}
                      </p>
                    </div>
                    <ChevronDown size={12} className="text-muted-foreground hidden lg:block" />
                  </motion.button>
                </div>
              </div>
            ) : (
              <Link to="/כניסה" aria-label="כניסה לחשבון">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center gap-2 py-2 px-4 rounded-xl bg-primary/10 hover:bg-primary/15 text-primary font-semibold text-[13px] transition-all"
                >
                  <User size={16} />
                  <span>כניסה</span>
                </motion.button>
              </Link>
            )}
          </div>

          {/* ═══ Shared Profile Dropdown (mobile + desktop) ═══ */}
          {user && (
            <div ref={profileMenuRef}>
              <AnimatePresence>
                {profileMenuOpen && (
                  <>
                    {/* Backdrop for mobile */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 bg-black/20 z-[60] md:hidden"
                      onClick={() => setProfileMenuOpen(false)}
                    />
                    <motion.div
                      initial={{ opacity: 0, y: -6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.97 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="fixed left-4 right-4 top-28 md:left-auto md:right-4 md:top-[72px] md:w-[300px] bg-popover rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15),0_0_0_1px_hsl(var(--border)/0.5)] overflow-hidden z-[70] max-h-[80vh] overflow-y-auto"
                    >
                      {/* User header with gradient */}
                      <div className="relative p-5 overflow-hidden">
                        <div className="absolute inset-0 gradient-primary opacity-[0.06]" />
                        <div className="absolute top-0 left-0 w-32 h-32 rounded-full bg-primary/10 blur-[60px] -translate-x-1/2 -translate-y-1/2" />
                        <div className="relative flex items-center gap-3.5">
                          <div className="w-12 h-12 rounded-2xl gradient-primary text-primary-foreground flex items-center justify-center text-lg font-black shrink-0 shadow-lg shadow-primary/25">
                            {(user.email || "?")[0].toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-bold text-popover-foreground truncate text-sm leading-tight">
                              {user.email?.split("@")[0] || "משתמש"}
                            </p>
                            <p className="text-[11px] text-muted-foreground truncate mt-0.5">{user.email}</p>
                          </div>
                        </div>
                      </div>

                      {/* Quick stats row */}
                      <div className="grid grid-cols-3 gap-px bg-border/30 mx-4 rounded-xl overflow-hidden mb-3">
                        {[
                          { icon: Package, label: "הזמנות", path: "/הזמנות", value: null },
                          { icon: Heart, label: "מועדפים", path: "/מועדפים", value: null },
                          { icon: Coins, label: "נקודות", path: "/פרופיל", value: pointsBalance.toLocaleString() },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { navigate(item.path); setProfileMenuOpen(false); }}
                            className="flex flex-col items-center gap-1 py-3 bg-popover hover:bg-muted/60 transition-all duration-200 group first:rounded-r-xl last:rounded-l-xl"
                          >
                            <div className="w-9 h-9 rounded-xl bg-muted/70 group-hover:bg-primary/10 flex items-center justify-center transition-all duration-200 group-hover:scale-105">
                              <item.icon size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            {item.value && (
                              <span className="text-[11px] font-black text-primary">{item.value}</span>
                            )}
                            <span className="text-[10px] font-medium text-muted-foreground group-hover:text-popover-foreground transition-colors leading-none">
                              {item.label}
                            </span>
                          </button>
                        ))}
                      </div>

                      {/* Menu links — include Profile link */}
                      <div className="px-2 pb-1">
                        {[
                          { icon: User, label: "הפרופיל שלי", path: "/פרופיל" },
                          { icon: Star, label: "הביקורות שלי", path: "/הביקורות-שלי" },
                          { icon: Heart, label: "רשימת בקשות", path: "/מועדפים" },
                          ...(isVendor
                            ? [{ icon: Store, label: "פורטל ספקים", path: "/פורטל-ספקים" }]
                            : [{ icon: Store, label: "כניסת ספקים", path: "/הפוך-לספק" }]),
                          { icon: Headphones, label: "שירות לקוחות", path: "/מרכז-עזרה" },
                          { icon: Settings, label: "הגדרות", path: "/הגדרות" },
                        ].map((item) => (
                          <button
                            key={item.label}
                            onClick={() => { navigate(item.path); setProfileMenuOpen(false); }}
                            className="w-full flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-muted/60 hover:shadow-[0_0_12px_hsl(var(--primary)/0.08)] transition-all duration-200 text-right group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-muted/50 group-hover:bg-primary/10 flex items-center justify-center transition-all duration-200 shrink-0">
                              <item.icon size={15} className="text-muted-foreground group-hover:text-primary transition-colors" />
                            </div>
                            <span className="text-[13px] font-medium text-popover-foreground flex-1">{item.label}</span>
                            <ChevronLeft size={13} className="text-muted-foreground/40 group-hover:text-muted-foreground group-hover:-translate-x-0.5 transition-all" />
                          </button>
                        ))}
                      </div>

                      {isAdmin && (
                        <div className="mx-2 mb-1">
                          <button
                            onClick={() => { navigate("/ניהול"); setProfileMenuOpen(false); }}
                            className="w-full flex items-center gap-3 py-2 px-3 rounded-xl bg-destructive/5 hover:bg-destructive/10 transition-all duration-200 text-right group"
                          >
                            <div className="w-8 h-8 rounded-lg bg-destructive/10 group-hover:bg-destructive/15 flex items-center justify-center transition-all shrink-0">
                              <ShieldCheck size={15} className="text-destructive" />
                            </div>
                            <span className="text-[13px] font-bold text-destructive flex-1">פאנל ניהול</span>
                            <ChevronLeft size={13} className="text-destructive/40" />
                          </button>
                        </div>
                      )}

                      <div className="border-t border-border/40 mx-2 my-1" />

                      <div className="px-2 pb-2">
                        <button
                          onClick={async () => { await signOut(); setProfileMenuOpen(false); navigate("/"); }}
                          className="w-full flex items-center gap-3 py-2 px-3 rounded-xl hover:bg-destructive/5 transition-all duration-200 group"
                        >
                          <div className="w-8 h-8 rounded-lg group-hover:bg-destructive/10 flex items-center justify-center transition-all shrink-0">
                            <LogOut size={15} className="text-muted-foreground group-hover:text-destructive transition-colors" />
                          </div>
                          <span className="text-[13px] font-medium text-muted-foreground group-hover:text-destructive transition-colors">התנתק</span>
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        {/* ═══ Mobile Search Overlay ═══ */}
        <AnimatePresence>
          {mobileSearchOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/30 z-[80] md:hidden"
                onClick={() => setMobileSearchOpen(false)}
              />
              <motion.div
                ref={mobileSearchRef}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="fixed top-0 left-0 right-0 z-[90] bg-card shadow-xl md:hidden"
              >
                <div className="flex items-center gap-2 p-3">
                   <form
                     className="relative flex-1"
                     onSubmit={handleSearchSubmit}
                   >
                    <input
                      type="text"
                      name="query"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setShowSearchPreview(true); }}
                      placeholder="חפש מוצרים, קטגוריות, מותגים..."
                      className="w-full rounded-xl border border-primary/40 bg-muted/30 py-3 pr-4 pl-12 text-sm outline-none focus:border-primary focus:bg-card focus:shadow-[0_0_0_3px_hsl(var(--primary)/0.1)] transition-all placeholder:text-muted-foreground/40"
                      autoFocus
                    />
                    <div className="absolute left-1.5 top-1/2 -translate-y-1/2">
                      <motion.button
                        type="submit"
                        aria-label="חיפוש"
                        whileTap={{ scale: 0.9 }}
                        className="gradient-primary rounded-lg p-2 text-primary-foreground shadow-sm"
                      >
                        <Search size={16} />
                      </motion.button>
                    </div>
                   </form>
                  <motion.button
                    whileTap={{ scale: 0.9 }}
                    onClick={() => { setMobileSearchOpen(false); setSearchQuery(""); }}
                    className="p-2 rounded-xl hover:bg-muted/60 transition-colors shrink-0"
                  >
                    <X size={20} className="text-muted-foreground" />
                  </motion.button>
                </div>
                {/* Mobile live preview */}
                {showSearchPreview && searchPreviewResults && searchPreviewResults.length > 0 && searchQuery.trim().length >= 2 && (
                  <div className="border-t border-border/20 max-h-[60vh] overflow-y-auto">
                    {searchPreviewResults.map((product) => (
                      <button
                        type="button"
                        key={product.id}
                        onPointerUp={(event) => {
                          event.preventDefault();
                          handleMobileProductSelect(product.id, product.name);
                        }}
                        onClick={(event) => {
                          if (event.detail === 0) handleMobileProductSelect(product.id, product.name);
                        }}
                        disabled={!!navigatingProductId}
                        aria-busy={navigatingProductId === product.id}
                        className="group relative flex w-full items-center gap-3 px-3 py-3 min-h-[56px] text-start border-b border-border/10 last:border-0 touch-manipulation cursor-pointer transition-all duration-150 hover:bg-muted/60 active:bg-primary/10 active:scale-[0.985] focus-visible:outline-none focus-visible:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-inset disabled:opacity-60"
                      >
                        <img
                          src={product.image_url || "/placeholder.svg"}
                          alt=""
                          className="w-10 h-10 rounded-lg object-contain bg-muted/10 shrink-0 ring-1 ring-border/20 transition-transform duration-150 group-active:scale-95"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium line-clamp-1 transition-colors group-hover:text-primary group-active:text-primary">{product.name}</p>
                          <p className="text-[10px] text-muted-foreground">{product.category}</p>
                        </div>
                        <ChevronLeft size={14} className="text-muted-foreground/50 shrink-0 transition-transform duration-150 group-hover:-translate-x-0.5 group-active:-translate-x-1" />

                        {navigatingProductId === product.id ? (
                          <Loader2 size={16} className="animate-spin text-primary shrink-0" />
                        ) : (
                          <span className="text-xs font-bold text-[hsl(var(--price))] shrink-0">₪{product.price}</span>
                        )}
                      </button>

                    ))}
                    <button
                      onClick={() => handleSearchSubmit()}
                      className="w-full px-3 py-2.5 text-xs font-medium text-primary hover:bg-primary/5 transition-colors flex items-center justify-center gap-1"
                    >
                      <Search size={12} />
                      הצג את כל התוצאות
                    </button>
                  </div>
                )}
              </motion.div>
            </>
          )}
        </AnimatePresence>

        {/* ── Mobile category bar ── */}
        <div className="md:hidden border-t border-border/20 overflow-x-auto scrollbar-hide bg-card/30">
          <div className="flex items-center justify-center gap-1 px-1.5 py-1">
            {categories.slice(0, 7).map((cat, i) => {
              const isActive = activeCatIndex === i;
              return (
                <motion.div key={cat.name} whileTap={{ scale: 0.88 }} transition={{ type: "spring", stiffness: 400, damping: 15 }}>
                  <Link
                    to={`/קטגוריה/${cat.slug}`}
                    className={`flex flex-col items-center gap-0.5 p-1 rounded-xl transition-colors group ${isActive ? "bg-primary/8" : ""}`}
                    title={cat.name}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                        isActive ? "gradient-primary text-primary-foreground shadow-sm shadow-primary/20" : "bg-muted/60 group-hover:bg-primary/10"
                      }`}
                    >
                      <cat.icon size={14} className={isActive ? "text-primary-foreground" : "text-foreground/70 group-hover:text-primary transition-colors"} />
                    </div>
                    <span className={`text-[10px] font-medium leading-tight transition-colors ${
                      isActive ? "text-primary font-bold" : "text-foreground/80"
                    }`}>
                      {cat.name}
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* ═══ Desktop category bar ═══ */}
        <div className="hidden lg:block border-t border-border/15">
          <div className="container">
            <div className="flex flex-wrap items-center gap-0">
              {/* All categories mega menu */}
              <div
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => { setMegaOpen(false); setHoveredCat(null); }}
              >
                <Link to="/קטגוריות" className="flex items-center gap-2 py-2.5 px-4 text-[13px] font-bold hover:text-primary transition-all rounded-lg hover:bg-primary/5 group border-l border-border/20">
                  <Menu size={14} className="text-primary" />
                  <span>כל הקטגוריות</span>
                  <motion.span animate={{ rotate: megaOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                    <ChevronDown size={11} className="text-muted-foreground" />
                  </motion.span>
                </Link>

                <AnimatePresence>
                {megaOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                      className="absolute top-full right-0 w-[780px] bg-popover rounded-2xl shadow-[0_25px_60px_-12px_rgba(0,0,0,0.15),0_0_0_1px_hsl(var(--border)/0.4)] z-50 overflow-hidden"
                    >
                      <div className="flex">
                        {/* Left sidebar: category list */}
                        <div className="w-[220px] bg-muted/30 border-l border-border/30 p-2">
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 pt-2 pb-2">קטגוריות</p>
                          {categories.map((cat, i) => (
                            <Link
                              key={cat.name}
                              to={`/קטגוריה/${cat.slug}`}
                              onClick={() => setMegaOpen(false)}
                              onMouseEnter={() => setHoveredCat(i)}
                              className={`flex items-center gap-2.5 py-2.5 px-3 rounded-xl transition-all duration-200 group/item ${
                                hoveredCat === i ? "bg-primary/8 text-primary" : "hover:bg-muted/70 text-popover-foreground"
                              }`}
                            >
                              <img src={cat.circleImage} alt={cat.name} className={`w-8 h-8 rounded-full object-cover transition-all duration-200 ring-1 shrink-0 ${
                                hoveredCat === i ? "ring-primary/40 scale-105" : "ring-border/30 group-hover/item:ring-primary/20"
                              }`} />
                              <span className="text-[13px] font-semibold flex-1">{cat.name}</span>
                              <ChevronLeft size={12} className={`transition-all ${hoveredCat === i ? "text-primary opacity-100 -translate-x-0.5" : "opacity-0"}`} />
                            </Link>
                          ))}
                        </div>

                        {/* Right content: subcategories */}
                        <div className="flex-1 p-6">
                          {hoveredCat !== null ? (
                              <div>
                                <div className="flex items-center gap-3 mb-5">
                                  <img src={categories[hoveredCat].circleImage} alt={categories[hoveredCat].name} className="w-10 h-10 rounded-full object-cover ring-2 ring-primary/20 shadow-md" />
                                  <div>
                                    <h3 className="text-base font-black text-popover-foreground">{categories[hoveredCat].name}</h3>
                                    <p className="text-[11px] text-muted-foreground">{categories[hoveredCat].subcategories.length} תתי-קטגוריות</p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-1.5">
                                {categories[hoveredCat].subcategories.map((sub) => (
                                    <div key={sub.name}>
                                      <Link
                                        to={`/קטגוריה/${categories[hoveredCat].slug}/${encodeURIComponent(sub.name)}`}
                                        onClick={() => setMegaOpen(false)}
                                        className="flex items-center gap-2.5 py-2.5 px-3 rounded-xl hover:bg-primary/5 transition-all duration-300 group/sub"
                                      >
                                        <div className="relative shrink-0">
                                          <SubcategoryCircle
                                            name={sub.name}
                                            emoji={sub.emoji}
                                            staticImage={sub.staticImage}
                                            dbImage={sub.dbImage}
                                            gradientColor={categories[hoveredCat].color}
                                            size="md"
                                            className="ring-1 ring-border/30 group-hover/sub:ring-primary/50 group-hover/sub:scale-110 group-hover/sub:shadow-[0_0_12px_hsl(var(--primary)/0.3)] transition-all duration-300"
                                          />
                                          <div className="absolute inset-0 rounded-full opacity-0 group-hover/sub:opacity-100 transition-opacity duration-300 bg-gradient-to-tr from-primary/10 to-transparent pointer-events-none" />
                                        </div>
                                        <span className="text-[13px] font-medium text-muted-foreground group-hover/sub:text-primary group-hover/sub:translate-x-[-2px] transition-all duration-300">{sub.name}</span>
                                      </Link>
                                    </div>
                                  ))}
                                </div>
                                <Link
                                  to={`/קטגוריה/${categories[hoveredCat].slug}`}
                                  onClick={() => setMegaOpen(false)}
                                  className="inline-flex items-center gap-1.5 mt-5 text-[12px] font-bold text-primary hover:underline"
                                >
                                  צפו בכל המוצרים
                                  <ChevronLeft size={12} />
                                </Link>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center">
                                <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
                                  <ArrowLeftRight size={24} className="text-muted-foreground/40" />
                                </div>
                                <p className="text-sm font-semibold text-muted-foreground/60">העבירו את העכבר על קטגוריה</p>
                                <p className="text-[11px] text-muted-foreground/40 mt-1">כדי לראות את תתי-הקטגוריות</p>
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Bottom bar */}
                      <div className="border-t border-border/30 bg-muted/20 px-6 py-3 flex items-center justify-between">
                        <Link
                          to="/קטגוריות"
                          onClick={() => setMegaOpen(false)}
                          className="text-[12px] font-bold text-primary hover:underline flex items-center gap-1"
                        >
                          לכל הקטגוריות
                          <ChevronLeft size={11} />
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Category links */}
              {categories.map((cat, i) => {
                const isActive = activeCatIndex === i;
                return (
                  <Link
                    key={cat.name}
                    to={`/קטגוריה/${cat.slug}`}
                    className={`relative py-2.5 px-3 text-[13.5px] font-medium transition-all group hover:drop-shadow-[0_0_8px_hsl(var(--primary)/0.4)] ${
                      isActive ? "text-primary drop-shadow-[0_0_6px_hsl(var(--primary)/0.3)]" : "text-foreground hover:text-primary"
                    }`}
                  >
                    {cat.name}
                    <span className={`absolute bottom-0 right-3 left-3 h-[2px] rounded-full bg-primary transition-transform origin-right ${
                      isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`} />
                  </Link>
                );
              })}

              <div className="w-px h-4 bg-border/40 mx-1" />

              {/* Blog */}
              <Link
                to="/בלוג"
                className="py-2.5 px-3 text-[13px] font-medium text-foreground/65 hover:text-foreground transition-colors relative group"
              >
                📝 בלוג
                <span className="absolute bottom-0 right-3 left-3 h-[2px] rounded-full bg-primary scale-x-0 group-hover:scale-x-100 transition-transform origin-right" />
              </Link>

              {/* Spacer */}
              <div className="flex-1" />

              {/* Language switcher moved to side tab */}

              {/* Theme toggle — prominent */}
              <motion.button
                onClick={toggleTheme}
                title={isDark ? "מצב יום" : "מצב לילה"}
                className="flex items-center gap-2 py-1.5 px-3 rounded-full border border-border/40 bg-muted/50 hover:bg-primary/10 hover:border-primary/30 transition-all group"
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.92 }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={isDark ? "sun" : "moon"}
                    initial={{ opacity: 0, rotate: -90 }}
                    animate={{ opacity: 1, rotate: 0 }}
                    exit={{ opacity: 0, rotate: 90 }}
                    transition={{ duration: 0.25 }}
                  >
                    {isDark ? <Sun size={16} className="text-yellow-400" /> : <Moon size={16} className="text-primary" />}
                  </motion.div>
                </AnimatePresence>
                <span className="text-[11px] font-bold text-muted-foreground group-hover:text-foreground transition-colors">
                  {isDark ? "יום" : "לילה"}
                </span>
              </motion.button>
            </div>
          </div>
        </div>
      </nav>

      {/* ═══ Mobile menu drawer ═══ */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="lg:hidden fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-background z-50 overflow-y-auto shadow-[0_0_60px_rgba(0,0,0,0.2)]"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-border/40">
                <img src={isDark ? nicegiftLogoDark : nicegiftLogo} alt="NiceGift" width={120} height={48} className="h-12 w-auto" />
                <motion.button
                  whileTap={{ scale: 0.85 }}
                  onClick={() => setMobileOpen(false)}
                  className="w-9 h-9 rounded-xl bg-muted/60 hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <X size={18} />
                </motion.button>
              </div>

              {/* User section */}
              {user ? (
                <div className="p-4 border-b border-border/40">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl gradient-primary text-primary-foreground flex items-center justify-center text-lg font-black shrink-0 shadow-lg shadow-primary/20">
                      {(user.email || "?")[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-bold text-foreground truncate text-sm">
                        {user.email?.split("@")[0] || "משתמש"}
                      </p>
                      <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { icon: Package, label: "הזמנות", path: "/הזמנות" },
                      { icon: Heart, label: "מועדפים", path: "/מועדפים" },
                      { icon: Coins, label: `${pointsBalance} נק'`, path: "/פרופיל" },
                    ].map((item) => (
                      <Link
                        key={item.label}
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-muted/40 hover:bg-muted/70 transition-all duration-200 group"
                      >
                        <div className="w-9 h-9 rounded-xl bg-background group-hover:bg-primary/10 flex items-center justify-center transition-all shadow-sm">
                          <item.icon size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <span className="text-[11px] font-semibold text-foreground">{item.label}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-4 border-b border-border/40">
                  <Link
                    to="/כניסה"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-3.5 rounded-xl gradient-primary text-primary-foreground font-bold text-sm shadow-lg shadow-primary/20"
                  >
                    <User size={18} />
                    כניסה / הרשמה
                  </Link>
                </div>
              )}

              {/* Account links */}
              {user && (
                <div className="px-3 py-3 border-b border-border/40">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2">חשבון</p>
                  {[
                    { icon: User, label: "הפרופיל שלי", path: "/פרופיל" },
                    { icon: Heart, label: "רשימת בקשות", path: "/מועדפים" },
                    { icon: Star, label: "הביקורות שלי", path: "/הביקורות-שלי" },
                    ...(isVendor
                      ? [{ icon: Store, label: "פורטל ספקים", path: "/פורטל-ספקים" }]
                      : [{ icon: Store, label: "כניסת ספקים", path: "/הפוך-לספק" }]),
                  ].map((item, i) => (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, x: 15 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                    >
                      <Link
                        to={item.path}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-muted/50 group-hover:bg-primary/10 flex items-center justify-center transition-all shrink-0">
                          <item.icon size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                        <span className="text-sm font-medium text-foreground flex-1">{item.label}</span>
                        <ChevronLeft size={14} className="text-muted-foreground/40 group-hover:text-muted-foreground transition-all" />
                      </Link>
                    </motion.div>
                  ))}
                  {isAdmin && (
                    <motion.div initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
                      <Link
                        to="/ניהול"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-destructive/5 hover:bg-destructive/10 transition-all duration-200 mt-1 group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                          <ShieldCheck size={16} className="text-destructive" />
                        </div>
                        <span className="text-sm font-bold text-destructive flex-1">פאנל ניהול</span>
                        <ChevronLeft size={14} className="text-destructive/40" />
                      </Link>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Categories — hierarchical tree */}
              <nav aria-labelledby="mobile-categories-heading" className="px-3 py-3 border-b border-border/40">
                <h2 id="mobile-categories-heading" className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2">קטגוריות</h2>
                <div className="space-y-0.5">
                  {categories.map((cat, i) => {
                    const isExpanded = mobileExpandedCats.has(i);
                    return (
                      <motion.div
                        key={cat.name}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.02 }}
                      >
                        <div className="flex items-center">
                          <Link
                            to={`/קטגוריה/${cat.slug}`}
                            onClick={() => setMobileOpen(false)}
                            className="flex items-center gap-2.5 py-2.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 group flex-1 min-w-0"
                          >
                            <img src={cat.circleImage} alt={cat.name} className="w-7 h-7 rounded-full object-cover ring-1 ring-border/30 group-hover:ring-primary/30 transition-all shrink-0" />
                            <span className="text-[13px] font-medium text-foreground truncate">{cat.name}</span>
                          </Link>
                          {cat.subcategories.length > 0 && (
                            <button
                              onClick={() => {
                                setMobileExpandedCats(prev => {
                                  const next = new Set(prev);
                                  if (next.has(i)) next.delete(i); else next.add(i);
                                  return next;
                                });
                              }}
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted/60 transition-all shrink-0"
                            >
                              <motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
                                <ChevronLeft size={14} className="text-muted-foreground" />
                              </motion.div>
                            </button>
                          )}
                        </div>
                        <AnimatePresence>
                          {isExpanded && cat.subcategories.length > 0 && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="pr-10 pb-1 space-y-0.5">
                                {cat.subcategories.map((sub) => (
                                  <Link
                                    key={sub.name}
                                    to={`/קטגוריה/${cat.slug}/${encodeURIComponent(sub.name)}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-2 py-2 px-3 rounded-lg text-[12px] text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all"
                                  >
                                    <SubcategoryCircle
                                      name={sub.name}
                                      emoji={sub.emoji}
                                      staticImage={sub.staticImage}
                                      dbImage={sub.dbImage}
                                      gradientColor={cat.color}
                                      size="sm"
                                      className="ring-1 ring-border/20"
                                    />
                                    {sub.name}
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </nav>

              {/* Extra links */}
              <div className="px-3 py-3 border-b border-border/40">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest px-3 mb-2">עוד</p>
                {[
                  { icon: Sparkles, label: "הפוך לספק", path: "/הפוך-לספק", accent: true },
                  { icon: Download, label: "התקן אפליקציה", path: "/התקנה" },
                  { icon: Headphones, label: "שירות לקוחות", path: "/מרכז-עזרה" },
                  { icon: Settings, label: "הגדרות", path: "/הגדרות" },
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2.5 px-3 rounded-xl hover:bg-muted/60 transition-all duration-200 group"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all ${item.accent ? "bg-primary/10" : "bg-muted/50 group-hover:bg-primary/10"}`}>
                      <item.icon size={16} className={item.accent ? "text-primary" : "text-muted-foreground group-hover:text-primary transition-colors"} />
                    </div>
                    <span className={`text-sm font-medium flex-1 ${item.accent ? "text-primary font-bold" : "text-foreground"}`}>{item.label}</span>
                    <ChevronLeft size={14} className="text-muted-foreground/40" />
                  </Link>
                ))}
              </div>

              {/* Sign out */}
              {user && (
                <div className="p-4">
                  <button
                    onClick={async () => { await signOut(); setMobileOpen(false); navigate("/"); }}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-border/50 text-muted-foreground hover:text-destructive hover:border-destructive/30 hover:bg-destructive/5 transition-all duration-200 font-medium text-sm"
                  >
                    <LogOut size={15} />
                    התנתק מהחשבון
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <Suspense fallback={null}>
        <VisualSearchModal open={visualSearchOpen} onClose={() => setVisualSearchOpen(false)} />
      </Suspense>
    </header>
  );
};

export default Navbar;
